import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import ConfirmDelete from "@/components/ConfirmDelete";
import { deleteWithUndo } from "@/lib/deleteWithUndo";
import CustomerSearch from "@/components/CustomerSearch";
import ProductSearch from "@/components/ProductSearch";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Trash2, Wrench, UserPlus, PackagePlus, X, MessageSquare,
  ChevronDown, ChevronUp, ArrowRight, RotateCcw, CheckCircle2, PauseCircle,
  Play, Truck, Search, Clock, ShieldCheck, FileText,
} from "lucide-react";
import { toast } from "sonner";

const empty = { customer_id: "", product_id: "", sale_id: "", service_type: "Maintenance", description: "", cost: 0 };
const TYPES = ["Repair", "Maintenance", "Inspection", "Warranty"];

const STATUS_META = {
  new:               { label: "New",                color: "bg-[#1A1F1C] text-[#F8F9FA]" },
  assigned:          { label: "Assigned",           color: "bg-blue-100 text-blue-300" },
  in_progress:       { label: "In Progress",        color: "bg-yellow-500/15 text-yellow-300" },
  waiting_for_parts: { label: "Waiting for Parts",  color: "bg-orange-100 text-orange-800" },
  ready:             { label: "Ready for Pickup",   color: "bg-purple-100 text-purple-800" },
  delivered:         { label: "Delivered",          color: "bg-emerald-100 text-[#39FF14]" },
  closed:            { label: "Closed",             color: "bg-[#39FF14]/10 text-[#39FF14]" },
};

const NEXT_ACTIONS = {
  new:               [{ target: "assigned",          label: "Mark Assigned",       icon: UserPlus }],
  assigned:          [{ target: "in_progress",       label: "Start Work",          icon: Play }],
  in_progress:       [
    { target: "ready",             label: "Mark Ready for Pickup", icon: CheckCircle2 },
    { target: "waiting_for_parts", label: "Waiting for Parts",     icon: PauseCircle, variant: "secondary" },
  ],
  waiting_for_parts: [
    { target: "in_progress", label: "Resume Work",           icon: Play },
    { target: "ready",       label: "Mark Ready for Pickup", icon: CheckCircle2 },
  ],
  ready:             [{ target: "delivered", label: "Mark Delivered", icon: Truck }],
  delivered:         [{ target: "closed",    label: "Close Service",   icon: CheckCircle2 }],
  closed:            [],
};

export default function Services() {
  const { user } = useAuth();
  const canAssign = user?.role === "admin" || user?.role === "service_manager";
  const canManageParts = ["admin", "service_manager", "spare_parts_manager"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [expandedId, setExpandedId] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [reopening, setReopening] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, total_pages: 0, page_size: 25 });
  const [loading, setLoading] = useState(false);

  // Search & filters
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [techFilter, setTechFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Keep URL in sync with the status filter (so deep-links from dashboard work both ways)
  useEffect(() => {
    if (statusFilter === "all") {
      if (searchParams.get("status")) { searchParams.delete("status"); setSearchParams(searchParams, { replace: true }); }
    } else if (searchParams.get("status") !== statusFilter) {
      searchParams.set("status", statusFilter); setSearchParams(searchParams, { replace: true });
    }
     
  }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (techFilter !== "all") params.set("technician_id", techFilter);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      // Technicians list is small (~50 max); load once. Customers/Products use typeahead.
      const needsTech = technicians.length === 0;
      const [s, t] = await Promise.all([
        api.get(`/services?${params.toString()}`),
        needsTech ? api.get("/users?role=technician") : Promise.resolve({ data: technicians }),
      ]);
      const items = Array.isArray(s.data) ? s.data : (s.data?.items || []);
      setRows(items);
      setPageInfo({
        page: s.data?.page ?? 1,
        page_size: s.data?.page_size ?? pageSize,
        total: s.data?.total ?? items.length,
        total_pages: s.data?.total_pages ?? 1,
      });
      setTechnicians(t.data);
    } finally { setLoading(false); }
  };

  // Reset to page 1 when filters/search change.
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [q, statusFilter, techFilter, fromDate, toDate]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, statusFilter, techFilter, fromDate, toDate, page]);

  const clearFilters = () => { setQ(""); setStatusFilter("all"); setTechFilter("all"); setFromDate(""); setToDate(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Select customer");
    try {
      const { data } = await api.post("/services", { ...form, cost: parseFloat(form.cost) || 0 });
      toast.success(`Service ${data.service_no} logged — customer notified`);
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const moveTo = async (s, target) => {
    try {
      await api.patch(`/services/${s.id}/status`, { status: target });
      toast.success(`Moved to ${STATUS_META[target]?.label || target}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Cannot move"); }
  };

  const confirmNoParts = async (s) => {
    try {
      await api.post(`/services/${s.id}/confirm-no-parts`);
      toast.success("Confirmed: no spare parts used");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const del = (s) =>
    deleteWithUndo({
      deletePath: `/services/${s.id}`,
      restorePath: `/services/${s.id}/restore`,
      label: `${s.service_no || s.service_type} — ${s.customer?.name || ""}`.trim(),
      entityLabel: "service",
      onChange: load,
    });

  const openJobCard = async (s) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/services/${s.id}/job-card.pdf`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Revoke after a delay so the new tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error("Could not open job card PDF");
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      <PageHeader
        title="Service & Maintenance"
        subtitle="Every job has a strict pipeline, permanent timeline, and auto SMS/WhatsApp on first-time transitions."
        exportEntity="services"
        testid="page-services-header"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setSelectedProduct(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-service-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
                <Plus size={16} className="mr-2" /> New Service Request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">Log Service Request</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Customer*</Label>
                  <CustomerSearch
                    testid="service-customer-search"
                    value={form.customer_id}
                    onChange={(c) => setForm({ ...form, customer_id: c?.id || "" })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Machine (optional)</Label>
                  <ProductSearch
                    testid="service-product-search"
                    value={form.product_id}
                    product={selectedProduct}
                    onChange={(p) => { setSelectedProduct(p); setForm({ ...form, product_id: p?.id || "" }); }}
                    placeholder="Search machine by brand, model, category…"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Estimated Cost</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
                <div className="col-span-2"><Label>Description*</Label><Textarea required data-testid="service-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid="service-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">Log Request</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Filters */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-md p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
          <Input
            data-testid="service-search"
            placeholder="Search service #, customer, phone, machine, technician…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 h-9" data-testid="service-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={techFilter} onValueChange={setTechFilter}>
          <SelectTrigger className="w-48 h-9" data-testid="service-tech-filter"><SelectValue placeholder="Technician" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All technicians</SelectItem>
            {technicians.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40 h-9" data-testid="service-from-date" />
        <span className="text-xs text-[#8A8F98]">to</span>
        <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40 h-9" data-testid="service-to-date" />
        {(q || statusFilter !== "all" || techFilter !== "all" || fromDate || toDate) && (
          <button onClick={clearFilters} className="text-xs text-[#8A8F98] hover:text-[#F8F9FA] underline" data-testid="clear-filters">
            Clear
          </button>
        )}
      </div>

      <div className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
            <tr>
              <th className="text-left px-4 py-3 w-8"></th>
              <th className="text-left px-4 py-3">Service #</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Machine</th>
              <th className="text-left px-4 py-3">Technician(s)</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="services-table-body">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-16 text-[#8A8F98]"><Wrench className="mx-auto mb-2" />No service requests match.</td></tr>
            )}
            {rows.map((s) => (
              <ServiceRow
                key={s.id}
                s={s}
                isOpen={expandedId === s.id}
                toggleOpen={() => setExpandedId(expandedId === s.id ? null : s.id)}
                canAssign={canAssign}
                canManageParts={canManageParts}
                onAssign={() => setAssigning(s)}
                onReopen={() => setReopening(s)}
                onMoveTo={(t) => moveTo(s, t)}
                onConfirmNoParts={() => confirmNoParts(s)}
                onJobCard={() => openJobCard(s)}
                onDelete={() => del(s)}
                onReload={load}
              />
            ))}
          </tbody>
        </table>
        <Pagination
          page={pageInfo.page}
          totalPages={pageInfo.total_pages}
          total={pageInfo.total}
          pageSize={pageInfo.page_size}
          onPageChange={setPage}
          loading={loading}
          testid="services-pagination"
        />
      </div>

      <AssignDialog
        service={assigning}
        technicians={technicians}
        onClose={() => setAssigning(null)}
        onDone={() => { setAssigning(null); load(); }}
      />
      <ReopenDialog
        service={reopening}
        onClose={() => setReopening(null)}
        onDone={() => { setReopening(null); load(); }}
      />
    </div>
  );
}

function ServiceRow({ s, isOpen, toggleOpen, canAssign, canManageParts, onAssign, onReopen, onMoveTo, onConfirmNoParts, onJobCard, onDelete, onReload }) {
  const meta = STATUS_META[s.status] || STATUS_META.new;
  const total = (Number(s.cost) || 0) + (Number(s.spare_parts_total) || 0);
  const actions = canAssign ? (NEXT_ACTIONS[s.status] || []) : [];
  const canReopen = canAssign && ["ready", "delivered", "closed"].includes(s.status);

  return (
    <>
      <tr className="border-b border-[#3A4640] hover:bg-[#0B0F0C]" data-testid={`service-row-${s.id}`}>
        <td className="px-4 py-3">
          <button onClick={toggleOpen} className="p-1 hover:bg-[#121815] rounded" data-testid={`service-expand-${s.id}`}>
            {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </td>
        <td className="px-4 py-3 font-mono text-xs font-semibold text-[#F8F9FA]" data-testid={`service-no-${s.id}`}>{s.service_no || "-"}</td>
        <td className="px-4 py-3">
          <div className="font-medium">{s.customer?.name || "-"}</div>
          <div className="text-xs text-[#8A8F98] font-mono">{s.customer?.phone}</div>
        </td>
        <td className="px-4 py-3 text-xs">
          {s.product?.brand ? `${s.product.brand} ${s.product.model || ""}` : <span className="text-[#8A8F98] italic">—</span>}
        </td>
        <td className="px-4 py-3">
          {(s.technicians || []).length === 0
            ? <span className="text-xs text-[#8A8F98] italic">Unassigned</span>
            : <div className="flex flex-wrap gap-1">{s.technicians.map((t) => <span key={t.id} className="text-xs bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded font-medium">{t.name}</span>)}</div>}
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs font-medium px-2 py-1 rounded ${meta.color}`} data-testid={`service-status-${s.id}`}>{meta.label}</span>
        </td>
        <td className="px-4 py-3 text-right font-mono">{formatCurrency(total)}</td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <button onClick={onJobCard} className="p-1.5 hover:bg-[#39FF14]/5 text-emerald-700 rounded border border-[#3A4640] mr-1" title="Job Card PDF" data-testid={`job-card-${s.id}`}>
            <FileText size={14} />
          </button>
          {canAssign && (
            <button onClick={onAssign} className="p-1.5 hover:bg-blue-500/10 text-blue-300 rounded border border-[#3A4640] mr-1" title="Assign technicians" data-testid={`assign-service-${s.id}`}>
              <UserPlus size={14} />
            </button>
          )}
          <ConfirmDelete itemLabel={s.service_no} entityLabel="service" onConfirm={onDelete} testid={`service-${s.id}`}>
            <button className="p-1.5 hover:bg-red-500/10 text-red-600 rounded border border-[#3A4640]" data-testid={`delete-service-${s.id}`}><Trash2 size={14} /></button>
          </ConfirmDelete>
        </td>
      </tr>
      {isOpen && (
        <tr data-testid={`service-details-${s.id}`}>
          <td colSpan={8} className="bg-[#0B0F0C] border-b border-[#3A4640] px-8 py-5">
            {/* Action bar */}
            {canAssign && (
              <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-[#3A4640] items-center">
                <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading font-semibold mr-2">Move to next stage:</div>
                {actions.length === 0 && (
                  <span className="text-xs text-[#8A8F98] italic">
                    {s.status === "closed" ? "Job is closed — use Reopen if this was a mistake." : "No further stage."}
                  </span>
                )}
                {actions.map((a) => (
                  <Button
                    key={a.target}
                    size="sm"
                    variant={a.variant === "secondary" ? "outline" : "default"}
                    onClick={() => onMoveTo(a.target)}
                    className={a.variant === "secondary" ? "" : "bg-[#39FF14] hover:bg-[#2ECC71]"}
                    data-testid={`move-${s.id}-${a.target}`}
                  >
                    <a.icon size={14} className="mr-1.5" />{a.label}
                    <ArrowRight size={12} className="ml-1.5 opacity-70" />
                  </Button>
                ))}
                {s.status === "in_progress" && !(s.spare_parts || []).length && !s.no_parts_confirmed && (
                  <Button size="sm" variant="outline" onClick={onConfirmNoParts} data-testid={`confirm-no-parts-${s.id}`}>
                    <ShieldCheck size={14} className="mr-1.5" /> Confirm: no parts used
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={onJobCard} data-testid={`job-card-bar-${s.id}`}>
                  <FileText size={14} className="mr-1.5" /> Job Card PDF
                </Button>
                {canReopen && (
                  <Button size="sm" variant="ghost" onClick={onReopen} className="text-red-600 hover:bg-red-500/10 hover:text-red-300 ml-auto" data-testid={`reopen-${s.id}`}>
                    <RotateCcw size={14} className="mr-1.5" /> Reopen service
                  </Button>
                )}
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-5">
              <div className="md:col-span-2">
                <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading font-semibold mb-1">Description</div>
                <div className="text-sm text-[#F8F9FA] mb-3">{s.description || "-"}</div>
                <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading font-semibold mb-1">Logged</div>
                <div className="text-xs text-[#8A8F98]">{formatDate(s.service_date)} by {s.created_by || "-"}</div>
              </div>
              <Timeline events={s.timeline || []} />
            </div>
            <SparePartsPanel service={s} canManageParts={canManageParts} onReload={onReload} />
          </td>
        </tr>
      )}
    </>
  );
}

function Timeline({ events }) {
  const sorted = [...(events || [])].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading font-semibold mb-2 flex items-center gap-1">
        <Clock size={12} /> Timeline ({sorted.length} events, permanent)
      </div>
      <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1" data-testid="service-timeline">
        {sorted.length === 0 && <div className="text-xs text-[#8A8F98]">No events yet.</div>}
        {sorted.map((e) => (
          <div key={e.id} className="text-xs bg-[#121815] border border-[#3A4640] rounded px-2.5 py-1.5 flex gap-2">
            <span className="font-mono text-[#8A8F98] shrink-0">{new Date(e.at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</span>
            <span className="flex-1">
              <span className="text-[#F8F9FA]">{e.message}</span>
              {e.actor && e.actor !== "system" && <span className="text-[#8A8F98] ml-1">· {e.actor}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SparePartsPanel({ service, canManageParts, onReload }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState(1);
  const [cost, setCost] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const parts = service.spare_parts || [];
  const partsTotal = service.spare_parts_total || 0;

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Part name required");
    setBusy(true);
    try {
      await api.post(`/services/${service.id}/spare-parts`, {
        name: name.trim(),
        quantity: parseInt(qty) || 1,
        unit_cost: parseFloat(cost) || 0,
        remarks: remarks.trim(),
      });
      setName(""); setQty(1); setCost(0); setRemarks("");
      onReload();
      toast.success("Spare part added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (partId) => {
    try {
      await api.delete(`/services/${service.id}/spare-parts/${partId}`);
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="mt-5 border-t border-[#3A4640] pt-4">
      <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading font-semibold mb-2 flex items-center gap-2">
        <span>Spare Parts Used ({parts.length}) — Subtotal {formatCurrency(partsTotal)}</span>
        {service.no_parts_confirmed && (
          <span className="text-[10px] normal-case tracking-normal bg-[#39FF14]/10 text-[#39FF14] px-1.5 py-0.5 rounded font-heading">
            No parts confirmed by manager
          </span>
        )}
      </div>
      {parts.length > 0 && (
        <div className="mb-3 bg-[#121815] border border-[#3A4640] rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0F0C] text-xs uppercase text-[#8A8F98] font-heading">
              <tr>
                <th className="text-left px-3 py-2">Part</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Unit ₹</th>
                <th className="text-right px-3 py-2">Line ₹</th>
                <th className="text-left px-3 py-2">Remarks</th>
                <th className="text-left px-3 py-2">Added by</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid={`parts-list-${service.id}`}>
              {parts.map((p) => (
                <tr key={p.id} className="border-t border-[#3A4640]">
                  <td className="px-3 py-2">{p.name}</td>
                  <td className="px-3 py-2 text-right font-mono">{p.quantity}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCurrency(p.unit_cost)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{formatCurrency((p.quantity || 0) * (p.unit_cost || 0))}</td>
                  <td className="px-3 py-2 text-xs text-[#8A8F98] max-w-xs truncate">{p.remarks || p.notes || "-"}</td>
                  <td className="px-3 py-2 text-xs text-[#8A8F98]">{p.added_by || "-"}</td>
                  <td className="px-3 py-2 text-right">
                    {canManageParts && (
                      <button onClick={() => remove(p.id)} className="p-1 hover:bg-red-500/10 text-red-600 rounded" data-testid={`remove-part-${p.id}`}><X size={12} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canManageParts ? (
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Part name*</Label>
            <Input data-testid={`part-name-${service.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Oil filter, bearing kit…" />
          </div>
          <div>
            <Label className="text-xs">Qty</Label>
            <Input type="number" min="1" data-testid={`part-qty-${service.id}`} value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Unit cost</Label>
            <Input type="number" min="0" data-testid={`part-cost-${service.id}`} value={cost} onChange={(e) => setCost(e.target.value)} />
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs">Remarks</Label>
            <Input data-testid={`part-remarks-${service.id}`} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="OEM / used / warranty" />
          </div>
          <Button type="submit" disabled={busy} data-testid={`add-part-${service.id}`} className="bg-[#39FF14] hover:bg-[#2ECC71]">
            <PackagePlus size={14} className="mr-1" /> Add part
          </Button>
        </form>
      ) : (
        <div className="text-xs text-[#8A8F98] italic">Only Service Manager, Spare Parts Manager, or Admin can log parts.</div>
      )}
    </div>
  );
}

function AssignDialog({ service, technicians, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSelected((service?.technicians || []).map((t) => t.id)); }, [service]);
  if (!service) return null;

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/services/${service.id}/assign`, { technician_ids: selected });
      toast.success(selected.length ? "Technician(s) assigned — messages sent" : "Technicians cleared");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Assign Technicians · {service.service_no}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-[#8A8F98] mb-2">
          Pick one or more technicians. On save, we&apos;ll SMS + WhatsApp them the job details and notify the customer.
        </div>
        <div className="max-h-72 overflow-y-auto border border-[#3A4640] rounded" data-testid="assign-tech-list">
          {technicians.length === 0 && (
            <div className="p-6 text-center text-[#8A8F98] text-sm">No technicians on your team yet. Add them under <b>Team → Service</b>.</div>
          )}
          {technicians.map((t) => {
            const on = selected.includes(t.id);
            return (
              <label key={t.id} className={`flex items-center gap-3 px-4 py-3 border-b border-[#3A4640] last:border-0 cursor-pointer hover:bg-[#0B0F0C] ${on ? "bg-green-50" : ""}`} data-testid={`tech-option-${t.id}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(t.id)} data-testid={`tech-checkbox-${t.id}`} />
                <div className="flex-1">
                  <div className="font-medium text-[#F8F9FA]">{t.name}</div>
                  <div className="text-xs text-[#8A8F98] font-mono">{t.phone || "no phone"}</div>
                </div>
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="assign-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="assign-save" className="bg-[#39FF14] hover:bg-[#2ECC71]">
            {busy ? "Saving…" : "Save & notify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReopenDialog({ service, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [service]);
  if (!service) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) return toast.error("Please enter at least 5 characters for the reason");
    setBusy(true);
    try {
      await api.post(`/services/${service.id}/reopen`, { reason: reason.trim() });
      toast.success(`Reopened ${service.service_no} — timeline updated`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reopen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Reopen {service.service_no}?</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-sm text-[#8A8F98]">
            Reopening moves this service back to <b>In Progress</b>. The current status (<b>{STATUS_META[service.status]?.label}</b>), your name, the date/time and your reason will be permanently logged in the timeline. No SMS/WhatsApp will be re-sent to the customer.
          </div>
          <div>
            <Label>Reason*</Label>
            <Textarea
              required
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer called — the same issue reappeared after 3 days of use."
              data-testid="reopen-reason-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="reopen-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="reopen-submit">
              {busy ? "Reopening…" : "Reopen service"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
